import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon, IconButton, InputText, Select, Table, Tooltip, } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { isPossiblePhoneNumber, isValidPhoneNumber, validatePossibleNumberWithReason, ValidationResult, } from 'lib-shared-phone-numbers';
import { useCallback, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getPhoneNumberErrorMessage } from '../validators/phoneNumber';
import styles from './forwardTableRow.module.scss';
export const TIMEOUT_OPTIONS = [
    { id: '10', name: '10 sec' },
    { id: '15', name: '15 sec' },
    { id: '20', name: '20 sec' },
    { id: '25', name: '25 sec' },
    { id: '30', name: '30 sec' },
    { id: '40', name: '40 sec' },
    { id: '60', name: '1 min' },
    { id: '120', name: '2 min' },
    { id: '180', name: '3 min' },
    { id: '240', name: '4 min' },
    { id: '300', name: '5 min' },
    { id: '600', name: '10 min' },
];
const ITEM_TYPE = 'number-row';
const ITEM_HEIGHT_DISCREPENCY = 10;
const INVALID_REASON_MAP = {
    [ValidationResult.UNKNOWN]: 'Please enter a valid phone number.',
    [ValidationResult.INVALID_COUNTRY_CODE]: 'Please enter the country code in the correct format (e.g. +1).',
    [ValidationResult.TOO_SHORT]: 'The phone number you entered is too short for this country code.',
    [ValidationResult.TOO_LONG]: 'The phone number you entered is too long for this country code.',
};
const ForwardTableRow = ({ number, numbers, index, onMoveNumber, onNumberUpdate, onNumberRemove, }) => {
    const ref = useRef(null);
    const [error, setError] = useState(null);
    const { type, phoneNumber, timeout } = number;
    const [firstTimeoutOption] = TIMEOUT_OPTIONS;
    const onPreventPropagation = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);
    const onNumberChange = useCallback(({ target: { value: phoneNumber } }) => {
        const updatedNumber = Object.assign(Object.assign({}, number), { phoneNumber });
        try {
            const phoneNumberErrorMessage = getPhoneNumberErrorMessage(phoneNumber);
            if (phoneNumberErrorMessage) {
                throw new Error(phoneNumberErrorMessage);
            }
            const isPossibleNumber = isPossiblePhoneNumber(phoneNumber);
            const isValidNumber = isValidPhoneNumber(phoneNumber);
            if (!isPossibleNumber || !isValidNumber) {
                const invalidNumberCase = validatePossibleNumberWithReason(phoneNumber) ||
                    ValidationResult.UNKNOWN;
                const error = INVALID_REASON_MAP[invalidNumberCase];
                throw new Error(error);
            }
            setError(null);
            updatedNumber.isValid = true;
        }
        catch (e) {
            setError(e.message);
            updatedNumber.isValid = false;
        }
        finally {
            onNumberUpdate(updatedNumber);
        }
    }, [number, onNumberUpdate, setError]);
    const onTimeoutChange = useCallback(({ id: value }) => {
        const timeout = value || null;
        onNumberUpdate(Object.assign(Object.assign({}, number), { timeout }));
    }, [number, onNumberUpdate]);
    const [, drop] = useDrop({
        accept: ITEM_TYPE,
        hover(item, monitor) {
            var _a;
            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex || !ref.current) {
                return;
            }
            // Determine rectangle on screen
            const hoverBoundingRect = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            // Determine mouse position
            const clientOffset = monitor.getClientOffset();
            // Get pixels to the top
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;
            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%
            // We add a 10px difference, because if item is exactly in the middle it will flicker.
            // Dragging downwards
            if (dragIndex < hoverIndex &&
                hoverClientY < hoverMiddleY + ITEM_HEIGHT_DISCREPENCY) {
                return;
            }
            // Dragging upwards
            if (dragIndex > hoverIndex &&
                hoverClientY > hoverMiddleY - ITEM_HEIGHT_DISCREPENCY) {
                return;
            }
            onMoveNumber(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    }, [onMoveNumber, index]);
    const [{ isDragging }, drag] = useDrag(() => ({
        type: ITEM_TYPE,
        item: { index },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    }), [index]);
    const isRemovable = numbers.length > 1;
    drag(drop(ref));
    return (_jsxs("tr", Object.assign({ ref: ref, className: classnames({ [styles.dragging]: isDragging }, styles.row) }, { children: [_jsx(Table.Td, Object.assign({ className: styles.handle }, { children: _jsx("span", { children: _jsx(Icon, { name: "bars" }) }) })), _jsx(Table.Td, Object.assign({ className: styles.type }, { children: type })), _jsx(Table.Td, { children: _jsx(InputText, { draggable: true, onDragStart: onPreventPropagation, value: phoneNumber !== null && phoneNumber !== void 0 ? phoneNumber : '', placeholder: "E.g +12058321018", onChange: onNumberChange, status: error ? 'error' : undefined, suffix: {
                        noWrap: true,
                        type: 'custom',
                        value: (_jsx("span", Object.assign({ className: styles.warning }, { children: error && (_jsx(Tooltip, Object.assign({ content: error, wrap: "span" }, { children: _jsx(Icon, { name: "info" }) }))) }))),
                    } }) }), _jsx(Table.Td, { children: _jsx(Select, { dropdownProps: { fixed: true }, items: TIMEOUT_OPTIONS, onSelect: onTimeoutChange, selectedItems: [timeout !== null && timeout !== void 0 ? timeout : firstTimeoutOption] }) }), _jsx(Table.Td, Object.assign({ className: styles.remove }, { children: isRemovable && (_jsx(IconButton, { onClick: onNumberRemove, icon: "close", variant: "tertiary" })) }))] })));
};
export default ForwardTableRow;
